// ---------------------------------------------------------------------------
// nativeGeolocation — a single abstraction over Capacitor's native geolocation
// plugin and the browser's navigator.geolocation. Use this instead of either
// API directly.
//
// On native iOS/Android the Capacitor plugin talks to CLLocationManager /
// FusedLocationProviderClient, which:
//   • Streams updates at a much higher rate than the WebView API
//   • Honours the system high-accuracy permission tier
//   • Keeps reporting while the screen is on but the WebView is sleeping
//
// On web (Lovable preview, desktop dev, eventual PWA) we fall back to
// navigator.geolocation so nothing breaks.
//
// The shape we expose:
//   • watchLocation(cb, err?, opts?) → Promise<WatchHandle> — start a stream
//   • getOneShotLocation(opts?) → Promise<LocationFix>      — single fix
//   • ensurePermission()        → Promise<PermissionState>   — pre-prompt
// ---------------------------------------------------------------------------

import { Capacitor } from "@capacitor/core";
import { Geolocation, type PositionOptions as CapPositionOptions } from "@capacitor/geolocation";

export interface LocationFix {
  lat: number;
  lng: number;
  /** Reported accuracy in meters (95% confidence radius). */
  accuracy?: number;
  /** Heading in degrees clockwise from true north, or null when unknown
   *  (e.g. stationary). Native plugins usually only populate this while
   *  the device is moving above a small speed threshold. */
  heading?: number | null;
  /** Speed in meters per second, or null when unknown. */
  speed?: number | null;
  altitude?: number | null;
  timestamp: number;
}

export interface WatchOptions {
  enableHighAccuracy?: boolean;
  /** How stale a cached fix is allowed to be (ms). */
  maximumAgeMs?: number;
  /** Per-fix timeout (ms). */
  timeoutMs?: number;
}

export interface WatchHandle {
  /** Stop the location stream. Idempotent — safe to call twice. */
  stop: () => void;
}

export type PermissionState = "granted" | "denied" | "prompt" | "unknown";

export const isNativeRuntime = (): boolean => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

/** READ the current permission state on native — never shows the system
 *  dialog. "prompt-with-rationale" is folded into "prompt".
 *
 *  Exists because the launch-time read went through ensurePermission, which
 *  REQUESTS when the answer is "prompt": a fresh install got the iOS location
 *  dialog on top of the sign-in screen before touching anything (measured in
 *  the Simulator, Sep 30, PLE-13). Reads use this; only a user's tap should
 *  reach ensurePermission. */
export const checkPermission = async (): Promise<PermissionState> => {
  try {
    const s = (await Geolocation.checkPermissions()).location;
    if (s === "granted" || s === "denied") return s;
    return "prompt";
  } catch {
    return "unknown";
  }
};

/** Ask the platform whether we already have permission, and REQUEST it when
 *  the answer is "prompt" — this can show the system dialog. On web we fall
 *  back to the Permissions API and return "unknown" on iOS Safari (which
 *  doesn't expose geolocation there). For a read with no dialog, use
 *  checkPermission. */
export const ensurePermission = async (): Promise<PermissionState> => {
  if (isNativeRuntime()) {
    try {
      const status = await Geolocation.checkPermissions();
      // location is the headline permission; coarse/precise both implied.
      const s = status.location;
      if (s === "granted" || s === "denied") return s;
      // "prompt" / "prompt-with-rationale" → ask.
      const req = await Geolocation.requestPermissions({ permissions: ["location"] });
      if (req.location === "granted") return "granted";
      if (req.location === "denied") return "denied";
      return "prompt";
    } catch {
      return "unknown";
    }
  }
  if (typeof navigator === "undefined" || !("permissions" in navigator)) return "unknown";
  try {
    const res = await (navigator.permissions as Permissions).query({ name: "geolocation" as PermissionName });
    return res.state as PermissionState;
  } catch {
    return "unknown";
  }
};

// ---------------------------------------------------------------------------
// DEAD-STREAM REPAIR (Morgan, Aug 30: "my app is not able to find my location")
//
// @capacitor/geolocation 8.2.0, GeolocationPlugin.swift, binds ONE Combine
// subscription to the location stream:
//
//   locationCancellable = locationService?.currentLocationPublisher
//       .catch { error -> AnyPublisher<IONGLOCPositionModel, Never> in
//           print("An error was found while retrieving the location: \(error)")
//           if case IONGLOCLocationError.locationUnavailable = error {
//               print("Location unavailable (likely due to backgrounding). Keeping watch callbacks alive.")
//           ...
//           return Empty<IONGLOCPositionModel, Never>().eraseToAnyPublisher()
//       }
//       .sink { ... }
//
// `catch` REPLACES the upstream — it does not resume it — and `Empty`
// completes immediately, so one `locationUnavailable` (the plugin's own comment
// blames backgrounding) finishes the subscription for good. Nothing is listening
// to the location stream after that. The callbacks stay in the array, which is
// what "keeping watch callbacks alive" means, but nothing will ever call them.
//
// It cannot re-bind itself for us:
//   • bindLocationPublisher() opens with `guard !locationInitialized else
//     { return }`, and that flag is still true — so the call every request makes
//     is a no-op.
//   • appDidBecomeActive() DOES rebuild the binding, but only
//     `if !watchCallbacks.isEmpty`. We hold a watch in exactly two places —
//     NavigationView during live turn-by-turn, and useHeading, which is gated on
//     the compass toggle and defaults off. On the trip-planning screen there is
//     no watch, so the repair never runs.
//
// CoreLocation keeps producing fixes the whole time; they are published into a
// dead chain. Every getCurrentPosition times out, the UI retries, and you get
// the logged signature: ~30 calls, no positions, no routing requests, until the
// app is force-quit — force-quit being what constructs a fresh plugin instance.
//
// The repair below needs no Swift and no patch. clearWatch is the plugin's own
// reset lever: when the LAST watch goes away it does exactly what we need —
//
//   if (callbackManager?.watchCallbacks.isEmpty) ?? false {
//       locationService?.stopMonitoringLocation()
//       locationCancellable?.cancel();  locationCancellable = nil
//       timeoutCancellable?.cancel();   timeoutCancellable = nil
//       locationInitialized = false
//   }
//
// — so registering a throwaway watch and immediately clearing it drops
// `locationInitialized` back to false, and the next request re-subscribes.
//
// KNOWN GAP: this is a no-op while a real watch is registered, because the
// callbacks array is then never empty. That is the live-navigation case, which
// is also the case the plugin repairs itself on foreground. A stream that dies
// mid-navigation while the app stays in the foreground still needs the nav watch
// torn down and rebuilt; not handled here.
// ---------------------------------------------------------------------------

/** Don't thrash the plugin when the real problem is a phone that cannot see the
 *  sky. One repair fixes a dead chain; if requests still fail after it, the
 *  fault is GPS, not us. */
const REBIND_COOLDOWN_MS = 30_000;
let lastRebindAt = 0;

/** Force the iOS plugin to rebuild its location subscription.
 *  @returns true when a rebind was actually attempted. */
export const rebindNativeLocation = async (): Promise<boolean> => {
  if (!isNativeRuntime()) return false;
  const now = Date.now();
  if (now - lastRebindAt < REBIND_COOLDOWN_MS) return false;
  lastRebindAt = now;
  try {
    // watchPosition resolves with its callback id as soon as the native call is
    // registered — it does not wait for a fix, which matters here because no fix
    // is coming until after the reset.
    const id = await Geolocation.watchPosition(
      { enableHighAccuracy: false, timeout: 1_000, maximumAge: 600_000 },
      () => { /* we want the registration, not the position */ },
    );
    await Geolocation.clearWatch({ id });
    console.log("[geo] location stream was dead — rebound the plugin");
    return true;
  } catch {
    return false;
  }
};

/** Test seam: forget the cooldown. */
export const __resetRebindCooldownForTests = () => { lastRebindAt = 0; };

const toFix = (
  coords: { latitude: number; longitude: number; accuracy?: number | null; heading?: number | null; speed?: number | null; altitude?: number | null },
  timestamp: number,
): LocationFix => ({
  lat: coords.latitude,
  lng: coords.longitude,
  accuracy: coords.accuracy ?? undefined,
  heading: typeof coords.heading === "number" && !Number.isNaN(coords.heading) && coords.heading >= 0 ? coords.heading : null,
  speed: typeof coords.speed === "number" && !Number.isNaN(coords.speed) && coords.speed >= 0 ? coords.speed : null,
  altitude: coords.altitude ?? null,
  timestamp,
});

/** Start a continuous location stream. Resolves once the watch is registered
 *  with the underlying platform — for native, that's after the async
 *  Geolocation.watchPosition call returns its watch id. */
export const watchLocation = async (
  onFix: (fix: LocationFix) => void,
  onError?: (err: Error) => void,
  opts: WatchOptions = {},
): Promise<WatchHandle> => {
  const enableHighAccuracy = opts.enableHighAccuracy ?? true;
  const timeout = opts.timeoutMs ?? 15_000;
  const maximumAge = opts.maximumAgeMs ?? 1000;

  if (isNativeRuntime()) {
    // The Capacitor plugin asks the system for permission lazily, but we
    // prompt up-front so the user sees the dialog when they tap "Go" rather
    // than mid-trip.
    const perm = await ensurePermission();
    if (perm === "denied") {
      onError?.(new Error("Location permission denied"));
      return { stop: () => {} };
    }
    let stopped = false;
    let watchId: string | undefined;
    const capOpts: CapPositionOptions = { enableHighAccuracy, timeout, maximumAge };
    try {
      watchId = await Geolocation.watchPosition(capOpts, (position, err) => {
        if (stopped) return;
        if (err) {
          onError?.(err instanceof Error ? err : new Error(String(err)));
          return;
        }
        if (!position) return;
        onFix(toFix(position.coords, position.timestamp));
      });
    } catch (e) {
      onError?.(e instanceof Error ? e : new Error(String(e)));
      return { stop: () => {} };
    }
    return {
      stop: () => {
        if (stopped) return;
        stopped = true;
        if (watchId) {
          // Fire-and-forget — clearWatch is a Promise but we don't care
          // about the result, the user already navigated away.
          void Geolocation.clearWatch({ id: watchId }).catch(() => {});
        }
      },
    };
  }

  // ---- Web fallback ----
  if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
    onError?.(new Error("Geolocation is not available in this environment"));
    return { stop: () => {} };
  }
  const id = navigator.geolocation.watchPosition(
    (pos) => onFix(toFix(pos.coords, pos.timestamp)),
    (err) => onError?.(new Error(err.message || "Geolocation error")),
    { enableHighAccuracy, timeout, maximumAge },
  );
  return {
    stop: () => {
      try { navigator.geolocation.clearWatch(id); } catch { /* noop */ }
    },
  };
};

/** One-shot location fix. Uses the same source priority as watchLocation. */
export const getOneShotLocation = async (opts: WatchOptions = {}): Promise<LocationFix> => {
  const enableHighAccuracy = opts.enableHighAccuracy ?? true;
  const timeout = opts.timeoutMs ?? 12_000;
  const maximumAge = opts.maximumAgeMs ?? 60_000;

  if (isNativeRuntime()) {
    const perm = await ensurePermission();
    if (perm === "denied") throw new Error("Location permission denied");
    try {
      const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy, timeout, maximumAge });
      return toFix(pos.coords, pos.timestamp);
    } catch (e) {
      // A failure here is as likely to be the dead subscription described above
      // as a genuinely absent fix, and the two are indistinguishable from JS —
      // both just time out. Repair once, then ask again; the cooldown stops this
      // becoming a retry loop when the phone really cannot see the sky.
      if (!(await rebindNativeLocation())) throw e;
      const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy, timeout, maximumAge });
      return toFix(pos.coords, pos.timestamp);
    }
  }
  if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
    throw new Error("Geolocation is not available in this environment");
  }
  return new Promise<LocationFix>((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve(toFix(pos.coords, pos.timestamp)),
      (err) => reject(new Error(err.message || "Geolocation error")),
      { enableHighAccuracy, timeout, maximumAge },
    );
  });
};
